<template>
  <component
    :is="tag"
    v-bind="input__bindings"
    :style="{ width: width }"
    :class="cssClasses"
    @click="onClick"
    ref="button"
  >
    <div v-if="$slots.left" :class="[$slots.left && $style.slotLeft]">
      <slot name="left" v-bind="slotBindings" />
    </div>

    <div v-if="$slots.default" :class="$style.caption">
      <slot></slot>
    </div>

    <div v-if="$slots.right" :class="[$slots.right && $style.slotRight]">
      <slot name="right" v-bind="slotBindings" />
    </div>
  </component>
</template>

<script>
/*
 *
 * b-button component
 *
 */
import Input from '../../mixins/input.js'
import InputSize from '../../mixins/input-size.js'

// themeable mixin factory
import makeThemeable from '../../utils/make-themeable.js'

// custom themeable mixin
const THEME_TYPES = [
  'default',
  'primary',
  'alert',
  'facebook',
  'mobile',
  'violet',
  'auth',
  'green',
  'transparent',
  'pure',
]
const Themeable = makeThemeable(THEME_TYPES, 'theme')

const SLOT_THEME_TYPES = [
  'default',
  'icon',
]
const ThemeableSlots = makeThemeable(SLOT_THEME_TYPES, 'slotTheme')

export default {
  name: 'b-button',

  mixins: [
    Input,
    InputSize,
    Themeable,
    ThemeableSlots,
  ],

  props: {
    width: {
      type: String,
    },
    tag: {
      type: String,
      default: 'button',
      validator: (v) => ['button', 'a'].includes(v),
    },
  },

  emits: ['click'],

  computed: {
    cssClasses () {
      return [].concat(
        [this.$style.button],
        // @see input mixin
        this.input__cssClasses,
        // @see input-size mixin
        this.size__cssClasses,
        // @see themeable mixin (factory)
        this.theme__cssClasses,
        this.slotTheme__cssClasses
      )
    },

    /*
     * bindings for left & right slot
     * @return Object
     */
    slotBindings () {
      return {
        disabled: this.disabled,
        theme: this.theme,
        slotTheme: this.slotTheme,
        size: this.size,
      }
    },
  },

  methods: {
    onClick (e) {
      if (this.$refs.button) {
        this.$refs.button.blur()
      }
      this.$emit('click', e)
    },
  },
}
</script>

<style lang="scss" module>
@layer legacy-views {

$primary-color: $color-primary;
$invalid-color: $color-primary;
$alert-color: $color-red;
$facebook-color: #3b5998;
$auth-color: var(--b-color-white);
$violet-color: var(--b-color-white);

.button {
  margin: 0;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: center;
  padding: 0;
}

.is--invalid {
  color: red;
  border-color: red;
}

.size {
  &--md {
    .caption {
      padding: 10px 16px;
      font-size: 12px;
    }
  }

  &--lg {
    min-width: 48px;
    min-height: 48px;

    .caption {
      padding: 15px 27px;
      font-size: 14px;
    }
  }

  &--sm {
    .caption {
      padding: 8px 12px;
      font-size: 8px;
    }
  }

  &--mb {
    .caption {
      padding: 6px 16px;
      font-size: 10px;
    }
  }
}

.theme {
  &--default {
    border: 1px solid $color-border-gray;
    border-radius: $border-radius;
    background-color: var(--color-white);
    transition: border-color 0.15s ease-in-out;
    cursor: pointer;
    color: $color-primary;

    .caption {
      text-transform: uppercase;
      font-weight: 800;
      text-align: center;
      line-height: 1;
      flex-grow: 1;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .slotLeft,
    .slotRight {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      flex-grow: 0;
      color: inherit;
    }
  }

  &--primary {
    @extend .theme--default;
    border-color: $primary-color;
    background: $primary-color;
    color: var(--color-white);
  }

  &--alert {
    @extend .theme--default;
    color: $alert-color;
  }

  &--facebook {
    @extend .theme--default;
    border-color: $facebook-color;
    background-color: $facebook-color;
    color: var(--color-white);
  }

  &--mobile {
    @extend .theme--default;
    border-radius: $border-radius-sm;
  }

  &--violet {
    @extend .theme--default;
    background-color: var(--b-color-violet);
    color: $violet-color;
  }

  &--auth {
    @extend .theme--default;
    background-color: $auth-color;
    color: var(--color-black);
    border-radius: 4px;

    .caption {
      text-transform: none;
      font-weight: 500;
      font-size: 0.875rem;
    }
  }

  &--green {
    @extend .theme--default;
    background-color: var(--b-color-green);
    color: var(--color-white);
  }

  &--transparent {
    @extend .theme--default;
    background-color: transparent;
    color: var(--color-black);
    .caption {
      text-transform: none;
      font-weight: 600;
    }
  }

  &--pure {
    @extend .theme--default;
    border: none;
    background-color: transparent;
    color: $color-primary;
  }
}

.slotTheme {
  &--default {
    .slotLeft {
      margin-right: 16px;
    }

    .slotRight {
      margin-left: 16px;
    }
  }

  &--icon {
    padding: 0;

    @at-root .size--sm#{&} {
      .slotRight,
      .slotLeft {
        padding: 2.5px;
      }
    }

    @at-root .size--md#{&} {
      .slotRight,
      .slotLeft {
        padding: 3.5px;
      }
    }

    @at-root .size--lg#{&} {
      .slotRight,
      .slotLeft {
        padding: 6.5px;
      }
    }
  }
}

.is--disabled {
  cursor: not-allowed;
  background-color: var(--color-white);
  color: var(--color-gray-11);

  &.theme {
    &--primary,
    &--facebook {
      background-color: var(--color-gray-13);
      color: var(--color-white);
      border: $border-gray;
    }
  }
}

}</style>
